import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useNavigate } from "react-router-dom";
import { useUserEditor } from "./useUserEditor";
import { useResetUserPassword } from "./useResetUserPassword";
import { ResetPasswordConfirmDialog } from "./ResetPasswordConfirmDialog";
import { GeneratedPasswordDialog } from "../../ui/GeneratedPasswordDialog";

export function UserEditor() {
  const navigate = useNavigate();
  const editor = useUserEditor(() => {
      void navigate("/users");
    });
  const reset = useResetUserPassword();
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const loadedRoles = editor.load.kind === "ready" ? editor.load.roles : [];
  const showsOnlyCurrentRole = editor.systemTarget && editor.currentRole !== null;
  const roleOptions = showsOnlyCurrentRole && editor.currentRole !== null ? [editor.currentRole] : loadedRoles;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {editor.mode === "create" ? "Создание пользователя" : "Редактирование пользователя"}
      </Typography>

      {editor.load.kind === "loading" ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : null}

      {editor.load.kind === "error" ? (
        <Alert severity="error">Не удалось загрузить данные: {editor.load.message}</Alert>
      ) : null}

      {editor.load.kind === "ready" ? (
        <Box
          component="form"
          display="flex" flexDirection="column" gap={2}
          onSubmit={(event) => {
            event.preventDefault();
            editor.submit();
          }}
        >
          {editor.systemTarget ? (
            <Alert severity="info">
              Системный пользователь: логин и имя можно изменить, роль и статус — нет.
            </Alert>
          ) : null}

          <TextField
            label="Логин"
            value={editor.login}
            onChange={(event) => editor.setLogin(event.target.value)}
            error={editor.fieldErrors.login !== undefined}
            helperText={editor.fieldErrors.login}
            fullWidth
          />

          <TextField
            label="Имя"
            value={editor.name}
            onChange={(event) => editor.setName(event.target.value)}
            error={editor.fieldErrors.name !== undefined}
            helperText={editor.fieldErrors.name}
            fullWidth
          />

          <TextField
            select
            label="Роль"
            value={editor.roleId}
            onChange={(event) => editor.setRoleId(event.target.value)}
            error={editor.fieldErrors.roleId !== undefined}
            helperText={editor.fieldErrors.roleId}
            disabled={editor.systemTarget}
            fullWidth
          >
            {roleOptions.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {`${role.name} (уровень ${role.level})`}
              </MenuItem>
            ))}
          </TextField>

          {editor.mode === "edit" ? (
            <TextField
              select
              label="Статус"
              value={editor.status}
              onChange={(event) => editor.setStatus(event.target.value === "inactive" ? "inactive" : "active")}
              disabled={editor.systemTarget}
              fullWidth
            >
              <MenuItem value="active">Активен</MenuItem>
              <MenuItem value="inactive">Деактивирован</MenuItem>
            </TextField>
          ) : null}

          {editor.fieldErrors.general !== undefined ? <Alert severity="error">{editor.fieldErrors.general}</Alert> : null}

          <Box display="flex" alignItems="center" gap={1}>
            {editor.mode === "edit" ? (
              <Button
                variant="outlined"
                color="warning"
                disabled={editor.submitting || reset.state.kind === "loading"}
                onClick={() => setResetConfirmOpen(true)}
              >
                Сбросить пароль
              </Button>
            ) : null}
            <Button type="submit" variant="contained" sx={{ ml: "auto" }} disabled={editor.submitting}>
              Сохранить
            </Button>
            <Button onClick={() => {
      void navigate("/users");
    }} disabled={editor.submitting}>
              Отмена
            </Button>
          </Box>
        </Box>
      ) : null}

      <ResetPasswordConfirmDialog
        open={resetConfirmOpen}
        login={editor.login}
        onConfirm={() => {
          setResetConfirmOpen(false);
          if (editor.userId !== undefined) {
            reset.resetPassword(editor.userId);
          }
        }}
        onCancel={() => setResetConfirmOpen(false)}
      />

      <GeneratedPasswordDialog
        open={editor.createdPassword !== null}
        login={editor.createdPassword !== null ? editor.createdPassword.login : ""}
        password={editor.createdPassword !== null ? editor.createdPassword.password : ""}
        onClose={editor.dismissCreatedPassword}
      />

      <GeneratedPasswordDialog
        open={reset.state.kind === "success"}
        login={reset.state.kind === "success" ? reset.state.login : ""}
        password={reset.state.kind === "success" ? reset.state.generatedPassword : ""}
        onClose={reset.reset}
      />

      <Snackbar open={reset.state.kind === "error"} autoHideDuration={6000} onClose={reset.reset}>
        <Alert severity="error" onClose={reset.reset}>
          {reset.state.kind === "error" ? reset.state.message : ""}
        </Alert>
      </Snackbar>
    </Box>
  );
}
